import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecordingContext } from '@context/RecordingContext';
import { useAudioLevelObserver, useLocalSessionId } from '@daily-co/daily-react';
import { FiberManualRecord, Pause, PlayArrow, WarningAmber } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';

interface RecordingStatusBarProps {
  isRecording: boolean;
  isStarting?: boolean;
  isPaused: boolean;
  onPause: () => void;
  onResume: () => void;
  hasRemoteParticipant: boolean;
  remoteParticipantLeft?: boolean;
  recordingFailed?: boolean;
  onStartRecording?: () => void;
  compact?: boolean;
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function getStatusMessage(
  isPaused: boolean,
  hasRemoteParticipant: boolean,
  remoteParticipantLeft: boolean,
): string {
  if (isPaused) return 'Recording paused';
  if (remoteParticipantLeft) return 'Recording';
  if (!hasRemoteParticipant) return 'Recording';
  return 'Recording session';
}

/**
 * Recording dot that reacts to audio level.
 * When audio is detected, the dot glows brighter and scales up slightly.
 * When silent, it dims to a subtle idle state. When paused, static yellow.
 */
function ReactiveDot({ level, isPaused }: { level: number; isPaused: boolean }) {
  // Clamp to 0-1 and apply a curve so low levels are still visible
  const intensity = isPaused ? 0 : Math.min(1, Math.pow(level * 2, 0.6));
  // Scale: 8px base, up to 10px when loud
  const size = 8 + intensity * 2;
  // Glow radius scales with intensity
  const glowRadius = 2 + intensity * 6;
  const glowOpacity = 0.3 + intensity * 0.5;

  return (
    <Box
      sx={{
        width: 10,
        height: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          bgcolor: isPaused ? 'warning.main' : 'error.main',
          opacity: isPaused ? 1 : 0.5 + intensity * 0.5,
          boxShadow: isPaused
            ? 'none'
            : `0 0 ${glowRadius}px rgba(244, 67, 54, ${glowOpacity})`,
          transition: 'all 80ms linear',
        }}
      />
    </Box>
  );
}

export function RecordingStatusBar({
  isRecording,
  isStarting = false,
  isPaused,
  onPause,
  onResume,
  hasRemoteParticipant,
  remoteParticipantLeft = false,
  recordingFailed = false,
  onStartRecording,
  compact: _compact = false,
}: RecordingStatusBarProps) {
  const [elapsed, setElapsed] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const { getRecordingTimes } = useRecordingContext();
  const localSessionId = useLocalSessionId();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useAudioLevelObserver(
    localSessionId ?? '',
    useCallback((volume: number) => {
      setAudioLevel(volume);
    }, []),
  );

  // Read authoritative elapsed time from RecordingContext every second
  useEffect(() => {
    if (!isRecording) {
      setElapsed(0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    const tick = () => {
      const times = getRecordingTimes();
      if (times) setElapsed(times.elapsedTime);
    };
    tick();
    intervalRef.current = setInterval(tick, 1000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isRecording, getRecordingTimes]);

  // Starting — show spinner + message
  if (!isRecording && isStarting) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          gap: 1,
          px: 2,
          py: 0.75,
          bgcolor: 'grey.800',
          borderRadius: 2,
        }}
      >
        <CircularProgress size={12} sx={{ color: 'grey.400' }} />
        <Typography variant="body2" sx={{ color: 'grey.400', fontWeight: 500 }}>
          Starting recording...
        </Typography>
      </Box>
    );
  }

  // Not recording — show failure bar or nothing
  if (!isRecording) {
    if (!recordingFailed) return null;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          gap: 1.5,
          px: 2,
          py: 0.75,
          bgcolor: 'grey.800',
          borderRadius: 2,
        }}
      >
        <WarningAmber sx={{ fontSize: 16, color: 'warning.main' }} />
        <Typography variant="body2" sx={{ color: 'warning.light', fontWeight: 500, flex: 1 }}>
          Not recording
        </Typography>
        {onStartRecording && (
          <IconButton
            onClick={onStartRecording}
            size="small"
            aria-label="Start recording"
            sx={{ color: 'error.main', p: 0.5 }}
          >
            <FiberManualRecord fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  }

  const statusMessage = getStatusMessage(isPaused, hasRemoteParticipant, remoteParticipantLeft);
  const pausedColor = 'warning.light';
  const activeColor = 'common.white';
  const textColor = isPaused ? pausedColor : activeColor;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: 1.5,
        px: 2,
        py: 0.75,
        bgcolor: 'grey.800',
        borderRadius: 2,
      }}
    >
      {/* Left: reactive dot + status message */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <ReactiveDot level={audioLevel} isPaused={isPaused} />
        <Typography
          variant="body2"
          sx={{ color: textColor, fontWeight: 500, whiteSpace: 'nowrap' }}
        >
          {statusMessage}
        </Typography>
      </Box>

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Right: timer + pause/resume */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
        <Typography
          variant="body2"
          sx={{
            color: textColor,
            fontFamily: 'monospace',
            fontWeight: 600,
            opacity: isPaused ? 0.7 : 1,
          }}
        >
          {formatDuration(elapsed)}
        </Typography>
        <IconButton
          onClick={isPaused ? onResume : onPause}
          size="small"
          aria-label={isPaused ? 'Resume recording' : 'Pause recording'}
          sx={{ color: activeColor, p: 0.5 }}
        >
          {isPaused ? <PlayArrow fontSize="small" /> : <Pause fontSize="small" />}
        </IconButton>
      </Box>
    </Box>
  );
}
